import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState, type CSSProperties } from 'react';
import { createPortal } from 'react-dom';
import { AnimatePresence, motion, useReducedMotion, useScroll, useTransform } from 'framer-motion';
import {
  ArrowDownRight,
  ArrowRight,
  ArrowUpRight,
  ChevronLeft,
  ChevronRight,
  Copy,
  Clapperboard,
  FolderKanban,
  House,
  Mail,
  Maximize2,
  Palette,
  Play,
  UserRound,
  X,
  ZoomIn,
  ZoomOut,
} from 'lucide-react';
import {
  aiImageWorks,
  aiVideoWorks,
  applicationProjects,
  capabilities,
  filmingWorks,
  gptImages,
  humanCcdImages,
  misteaImages,
  robotVacuumImages,
  variantMeta,
  videoItems,
  type Variant,
} from './data';
import type { PortfolioMediaItem } from './portfolioMedia';
import { GptImageDetailPage, ProjectDetailPage, ToolDetailPage } from './DetailPages';
import KineticHero from './components/KineticHero';
import ProjectDemoView from './components/ProjectDemoView';
import { projectDemos } from './projectDemos';
import { projectViewerAssets } from './projectViewerAssets';

const variants: Variant[] = ['professional', 'creator', 'workflow'];
const ORIGINAL_RHYTHM_PATH = '/review/original-rhythm';
const HISTORICAL_VARIANTS_PATH = '/review/variants';
const ENABLE_3D_HERO = true;

const VariantContext = createContext<Variant>('professional');

const motionProfiles = {
  professional: {
    duration: 0.48,
    ease: [0.22, 0.78, 0.2, 1] as [number, number, number, number],
    reveal: { opacity: 0, y: 22 },
    hover: -4,
    spring: { stiffness: 190, damping: 28, mass: 0.34 },
    pointer: [34, 22] as const,
  },
  creator: {
    duration: 0.86,
    ease: [0.16, 1, 0.3, 1] as [number, number, number, number],
    reveal: { opacity: 0, y: 48, rotate: -0.45 },
    hover: -10,
    spring: { stiffness: 88, damping: 15, mass: 0.72 },
    pointer: [70, 46] as const,
  },
  workflow: {
    duration: 0.64,
    ease: [0.4, 0, 0.2, 1] as [number, number, number, number],
    reveal: { opacity: 0, x: -26 },
    hover: -6,
    spring: { stiffness: 155, damping: 25, mass: 0.44 },
    pointer: [44, 28] as const,
  },
} as const;

function useActiveVariant() {
  return useContext(VariantContext);
}

function useMediaQuery(query: string) {
  const [matches, setMatches] = useState(() => window.matchMedia(query).matches);

  useEffect(() => {
    const mediaQuery = window.matchMedia(query);
    const update = () => setMatches(mediaQuery.matches);
    update();
    mediaQuery.addEventListener('change', update);
    return () => mediaQuery.removeEventListener('change', update);
  }, [query]);

  return matches;
}

function getInitialVariant(): Variant {
  if (window.location.pathname.replace(/\/+$/, '') !== HISTORICAL_VARIANTS_PATH) return 'professional';
  const selected = new URLSearchParams(window.location.search).get('variant');
  return variants.includes(selected as Variant) ? selected as Variant : 'professional';
}

function SectionHeader({ index, kicker, title, titleNote, description }: {
  index: string;
  kicker: string;
  title: string;
  titleNote?: string;
  description: string;
}) {
  const variant = useActiveVariant();
  const profile = motionProfiles[variant];
  return (
    <motion.header
      className="section-header"
      initial={profile.reveal}
      whileInView={{ opacity: 1, x: 0, y: 0, rotate: 0 }}
      viewport={{ once: true, amount: 0.22 }}
      transition={{ duration: profile.duration, ease: profile.ease }}
    >
      <div className="section-index"><span>{index}</span><span>{kicker}</span></div>
      <h2>{title}</h2>
      {titleNote && <span className="section-title-note">{titleNote}</span>}
      <p>{description}</p>
    </motion.header>
  );
}

function VariantSwitcher({ active, onChange }: { active: Variant; onChange: (value: Variant) => void }) {
  return (
    <div className="variant-switcher" aria-label="视觉方案切换">
      {variants.map((variant) => (
        <button
          key={variant}
          type="button"
          className={variant === active ? 'is-active' : ''}
          onClick={() => onChange(variant)}
          aria-pressed={variant === active}
        >
          <span>{variantMeta[variant].code}</span>
          <span>{variantMeta[variant].label}</span>
        </button>
      ))}
    </div>
  );
}

function PortfolioIntro({ criticalProgress, onComplete }: { criticalProgress: number; onComplete: () => void }) {
  const reduceMotion = useReducedMotion();
  const [visible, setVisible] = useState(true);
  const criticalProgressRef = useRef(criticalProgress);
  const progressRef = useRef(reduceMotion ? 100 : 8);
  const fillRef = useRef<HTMLDivElement>(null);
  const numberRef = useRef<HTMLElement>(null);

  useEffect(() => {
    criticalProgressRef.current = criticalProgress;
  }, [criticalProgress]);

  useEffect(() => {
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    let frame = 0;
    let exitTimer = 0;

    const finish = () => {
      document.body.style.overflow = previousOverflow;
      onComplete();
      setVisible(false);
    };

    if (reduceMotion) {
      exitTimer = window.setTimeout(finish, 160);
    } else {
      const startedAt = performance.now();
      const minDurationMs = 1100;
      const maxDurationMs = 2400;

      const update = (now: number) => {
        const elapsed = now - startedAt;
        const isReady = criticalProgressRef.current >= 100;
        
        // Continuous natural curve that steadily advances without flat plateaus
        const naturalTimeProgress = Math.min(94, 8 + 86 * (1 - Math.exp(-elapsed / 700)));
        
        let target = naturalTimeProgress;
        if (isReady && elapsed >= minDurationMs) {
          target = 100;
        } else if (isReady) {
          const readyRatio = Math.min(1, elapsed / minDurationMs);
          target = Math.max(naturalTimeProgress, 8 + 92 * readyRatio);
        }
        
        const current = progressRef.current;
        if (target >= 100) {
          const step = Math.max(2.8, (100 - current) * 0.24);
          progressRef.current = Math.min(100, current + step);
        } else {
          const distance = target - current;
          const step = distance > 0 ? Math.max(0.32, distance * 0.14) : 0.22;
          progressRef.current = Math.min(96, current + step);
        }

        if (fillRef.current) {
          fillRef.current.style.transform = `scaleX(${progressRef.current / 100})`;
        }
        if (numberRef.current) {
          numberRef.current.textContent = String(Math.round(progressRef.current)).padStart(2, '0');
        }

        if (progressRef.current >= 100 || elapsed >= maxDurationMs) {
          if (fillRef.current) fillRef.current.style.transform = 'scaleX(1)';
          if (numberRef.current) numberRef.current.textContent = '100';
          exitTimer = window.setTimeout(finish, 120);
          return;
        }
        frame = window.requestAnimationFrame(update);
      };
      frame = window.requestAnimationFrame(update);
    }

    return () => {
      window.cancelAnimationFrame(frame);
      window.clearTimeout(exitTimer);
      document.body.style.overflow = previousOverflow;
    };
  }, [reduceMotion]);

  return (
    <AnimatePresence>
      {visible && (
        <motion.div
          className="portfolio-intro"
          role="status"
          aria-live="polite"
          aria-label="Loading portfolio"
          initial={{ opacity: 1 }}
          exit={{ opacity: 0, scale: 1.02 }}
          transition={{ duration: reduceMotion ? .12 : .45, ease: [.76, 0, .24, 1] }}
        >
          <motion.div
            className="portfolio-intro-card"
            initial={reduceMotion ? false : { opacity: 0, scale: 0.95, y: 14 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            transition={{ duration: .45, ease: [.22, 1, .36, 1] }}
          >
            <div className="portfolio-intro-track" aria-hidden="true">
              <div className="portfolio-intro-plasma-waves" />
              <div
                ref={fillRef}
                className="portfolio-intro-fill"
                style={{ transform: `scaleX(${progressRef.current / 100})` }}
              >
                <div className="portfolio-intro-laser-crest">
                  <svg
                    className="laser-jagged-svg"
                    viewBox="0 0 100 150"
                    preserveAspectRatio="none"
                  >
                    <defs>
                      <filter id="laser-intense-glow" x="-50%" y="-50%" width="200%" height="200%">
                        <feGaussianBlur in="SourceGraphic" stdDeviation="6" result="blur1" />
                        <feGaussianBlur in="SourceGraphic" stdDeviation="14" result="blur2" />
                        <feMerge>
                          <feMergeNode in="blur2" />
                          <feMergeNode in="blur1" />
                          <feMergeNode in="SourceGraphic" />
                        </feMerge>
                      </filter>
                    </defs>
                    <path
                      className="laser-wave-halo"
                      d="M10,0 L42,18 L12,36 L56,60 L18,84 L60,112 L14,132 L46,150"
                      fill="none"
                      stroke="#00f0ff"
                      strokeWidth="20"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      opacity="0.75"
                      filter="url(#laser-intense-glow)"
                    />
                    <path
                      className="laser-wave-core-glow"
                      d="M10,0 L42,18 L12,36 L56,60 L18,84 L60,112 L14,132 L46,150"
                      fill="none"
                      stroke="#8de7c4"
                      strokeWidth="8"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      opacity="0.95"
                    />
                    <path
                      className="laser-wave-crest-line"
                      d="M10,0 L42,18 L12,36 L56,60 L18,84 L60,112 L14,132 L46,150"
                      fill="none"
                      stroke="#ffffff"
                      strokeWidth="3.5"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                  <div className="laser-forward-beam" />
                  <div className="laser-spark-top" />
                  <div className="laser-spark-bottom" />
                </div>
              </div>
            </div>
            <div className="portfolio-intro-body">
              <div className="portfolio-intro-info">
                <span className="portfolio-intro-tag">NEURAL SYNC</span>
                <small className="portfolio-intro-subtag">ADAPTIVE INTERFACE STREAM</small>
              </div>
              <div className="portfolio-intro-number">
                <strong ref={numberRef}>{String(Math.round(progressRef.current)).padStart(2, '0')}</strong>
                <small>%</small>
              </div>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}

function Navigation({ variant, onVariantChange, rhythmBaseline }: {
  variant: Variant;
  onVariantChange: (value: Variant) => void;
  rhythmBaseline: boolean;
}) {
  const [activeSection, setActiveSection] = useState('home');
  const [scrolled, setScrolled] = useState(false);
  const links = [
    ['HOME', 'home'],
    ['PROJECTS', 'projects'],
    ['GPT-IMAGE', 'gpt-image'],
    ['VIDEO', 'video'],
    ['ABOUT', 'about'],
  ] as const;

  useEffect(() => {
    let frame = 0;
    const updateActive = () => {
      cancelAnimationFrame(frame);
      frame = requestAnimationFrame(() => {
        const sections = links
          .map(([, target]) => document.getElementById(target))
          .filter((section): section is HTMLElement => Boolean(section))
          .sort((a, b) => a.offsetTop - b.offsetTop);
        const marker = window.scrollY + window.innerHeight * 0.28;
        const current = sections.reduce((active, section) => section.offsetTop <= marker ? section : active, sections[0]);
        if (current?.id) setActiveSection(current.id);
        setScrolled(window.scrollY > 48);
      });
    };
    updateActive();
    window.addEventListener('scroll', updateActive, { passive: true });
    window.addEventListener('resize', updateActive);
    window.addEventListener('hashchange', updateActive);
    return () => {
      cancelAnimationFrame(frame);
      window.removeEventListener('scroll', updateActive);
      window.removeEventListener('resize', updateActive);
      window.removeEventListener('hashchange', updateActive);
    };
  }, []);

  return (
    <>
      <nav className={`top-nav ${scrolled ? 'is-scrolled' : ''}`} aria-label="主导航" data-current={activeSection}>
        <a className="brand-mark" href="#home" aria-label="MSN 首页" onClick={() => setActiveSection('home')}>MSN<span>®</span><small>AI PORTFOLIO</small></a>
        <div className="nav-links">
          {links.map(([label, target], index) => (
            <a
              key={target}
              href={`#${target}`}
              className={activeSection === target ? 'is-current' : ''}
              aria-current={activeSection === target ? 'location' : undefined}
              data-step={String(index + 1).padStart(2, '0')}
              onClick={() => setActiveSection(target)}
            >
              {label}
            </a>
          ))}
        </div>
        <a className="nav-cta" href="#projects" onClick={() => setActiveSection('projects')}>查看项目 <ArrowDownRight aria-hidden="true" /></a>
      </nav>
      {rhythmBaseline
        ? import.meta.env.DEV && <div className="review-badge" aria-label="当前为唯一工作基线"><strong>MSN PORTFOLIO</strong><span>WORKING BASELINE / LOCAL DEV</span></div>
        : <VariantSwitcher active={variant} onChange={onVariantChange} />}
    </>
  );
}

function Hero({ variant, workingBaseline = false, onLoadProgress }: {
  variant: Variant;
  workingBaseline?: boolean;
  onLoadProgress?: (progress: number) => void;
}) {
  const profile = motionProfiles[variant];
  const reduceMotion = useReducedMotion();
  const [time, setTime] = useState(() => new Date());
  const [activeMedia, setActiveMedia] = useState<number | null>(null);

  useEffect(() => {
    const timer = window.setInterval(() => setTime(new Date()), 30_000);
    return () => window.clearInterval(timer);
  }, []);

  const showcaseItems = videoItems.slice(0, 8);

  return (
    <section id="home" className="hero hero-reference-layout" aria-label="LINZEXIN 作品集首屏">
      <div className="hero-console">
        <motion.header
          className="hero-reference-heading"
          initial={false}
        >
          <span>LINZEXIN / CREATIVE SYSTEMS</span>
          <h1 aria-label="LINZEXIN">
            {[...'LINZEXIN'].map((letter, index) => (
              <motion.span
                key={`${letter}-${index}`}
                aria-hidden="true"
                initial={reduceMotion ? false : { opacity: 0, y: 14 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: .42, delay: .04 + index * .032, ease: profile.ease }}
              >
                {letter}
              </motion.span>
            ))}
          </h1>
          <motion.strong
            className="hero-reference-title-tail"
            initial={reduceMotion ? false : { opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: .54, delay: .2, ease: profile.ease }}
          >
            <span>AI VISUAL DIRECTOR</span>
            <i aria-hidden="true">×</i>
            <span>WORKFLOW BUILDER</span>
          </motion.strong>
          <motion.p
            initial={reduceMotion ? false : { opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: .54, delay: .34, ease: profile.ease }}
          >
            <span>AI 视频 · 视觉设计 · 自动化工作流</span>
            <span>把创意拆成可复制、可协作、可交付的生产系统</span>
          </motion.p>
        </motion.header>

        <time className="hero-clock" dateTime={time.toISOString()}>
          <span className="hero-clock-status" aria-hidden="true" />
          <span className="hero-clock-copy">
            <strong>{new Intl.DateTimeFormat('zh-CN', {
              hour: '2-digit',
              minute: '2-digit',
              hour12: false,
            }).format(time)}</strong>
            <small>CN · UTC+8</small>
          </span>
        </time>

        <motion.figure
          className="hero-feature-video"
          initial={reduceMotion ? false : { opacity: 0, y: 26, scale: .975 }}
          animate={{ opacity: 1, y: 0, scale: 1 }}
          transition={{ duration: profile.duration + .28, delay: .08, ease: profile.ease }}
        >
          <video
            autoPlay
            muted
            loop
            playsInline
            preload="metadata"
            disablePictureInPicture
            poster="/assets/hero-film/linzexin-ai-cat-hero-poster-enhanced.jpg"
            aria-label="LINZEXIN AI 猫咪创作舱首屏影片"
            onLoadStart={() => onLoadProgress?.(35)}
            onLoadedMetadata={() => onLoadProgress?.(72)}
            onLoadedData={() => onLoadProgress?.(100)}
            onError={() => onLoadProgress?.(100)}
          >
            <source src="/assets/hero-film/linzexin-ai-cat-hero-4k.mp4" type="video/mp4" />
          </video>
          <figcaption><span>AI CAT / HERO FILM</span><strong>CREATIVE SYSTEMS ONLINE</strong></figcaption>
        </motion.figure>

        <motion.nav
          className="hero-project-rail"
          aria-label="精选项目"
          initial={reduceMotion ? false : { opacity: 0, y: 28 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: profile.duration, delay: .22, ease: profile.ease }}
        >
          <div className="hero-project-track">
            {[0, 1].map((loopIndex) => (
              <div
                key={loopIndex}
                className="hero-project-group"
                aria-hidden={loopIndex === 1 ? true : undefined}
              >
                {showcaseItems.map((item, index) => (
                  <a
                    key={`${loopIndex}-${item.title}`}
                    className={index === 0 ? 'is-active' : ''}
                    href="#video"
                    tabIndex={loopIndex === 1 ? -1 : undefined}
                    onClick={(event) => {
                      event.preventDefault();
                      setActiveMedia(index);
                    }}
                  >
                    <img src={item.poster} alt="" />
                    <span>{item.category}</span>
                    <strong>{item.title}</strong>
                    {index === 0 && <small><i style={{ '--progress': '72%' } as CSSProperties} />72% / SELECTED</small>}
                  </a>
                ))}
              </div>
            ))}
          </div>
        </motion.nav>

        <nav className="hero-dock" aria-label="快捷导航">
          <a href="#projects" aria-label="核心项目" data-label="PROJECTS"><FolderKanban /></a>
          <a href="#video" aria-label="AI 视频" data-label="AI VIDEO"><Clapperboard /></a>
          <a href="#gpt-image" aria-label="视觉生成" data-label="VISUAL"><Palette /></a>
          <a className="is-current" href="#home" aria-label="首页" data-label="HOME"><House /></a>
          <a href="#about" aria-label="关于我" data-label="ABOUT"><UserRound /></a>
          <a href="#contact" aria-label="联系我" data-label="CONTACT"><Mail /></a>
        </nav>
      </div>
      {activeMedia !== null && (
        <PortfolioMediaModal
          items={videoItems}
          index={activeMedia}
          onClose={() => setActiveMedia(null)}
          onMove={(next) => setActiveMedia((next + videoItems.length) % videoItems.length)}
        />
      )}
    </section>
  );
}

function ProjectVisual({ project }: { project: typeof aiImageWorks[number] }) {
  const demo = projectDemos[project.slug];
  if (demo) return <ProjectDemoView name={project.name} scenes={demo.scenes} {...projectViewerAssets[project.slug]} />;
  return (
    <div className="project-media media-count-1">
      <img src={project.images[0]} alt={`${project.name} 项目封面`} loading="lazy" decoding="async" />
    </div>
  );
}

function CoreProjects({ rhythmBaseline = false }: { rhythmBaseline?: boolean }) {
  const variant = useActiveVariant();
  const profile = motionProfiles[variant];

  const handleCardPointerMove = (e: React.PointerEvent<HTMLElement>) => {
    const rect = e.currentTarget.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const y = e.clientY - rect.top;
    e.currentTarget.style.setProperty('--mouse-x', `${x}px`);
    e.currentTarget.style.setProperty('--mouse-y', `${y}px`);
    e.currentTarget.style.setProperty('--mouse-active', '1');
  };

  const handleCardPointerLeave = (e: React.PointerEvent<HTMLElement>) => {
    e.currentTarget.style.setProperty('--mouse-active', '0');
  };

  return (
    <section
      id="projects"
      className={`core-section ${rhythmBaseline ? 'dark-section projects-dark-zone' : 'light-section'}`}
    >
      <SectionHeader
        index="03"
        kicker="精选 AI 项目"
        title="AI工作流与成果"
        titleNote="精选AI项目与落地流程"
        description="从 AI 工作流到原创内容与本地应用，透过实际界面和动态演示，看见想法如何变成可使用的成果。"
      />
      <div className="project-stack">
        {aiImageWorks.map((project, index) => (
          <article
            key={project.number}
            className="project-card"
            data-node={project.number}
            style={{ '--stack-index': index } as CSSProperties}
            onPointerMove={handleCardPointerMove}
            onPointerLeave={handleCardPointerLeave}
          >
            <div className="project-card-head">
              <span className="project-number">{project.number}</span>
              <div><span className="project-tag">{project.tag}</span><h3>{project.name}</h3></div>
            </div>
            <ProjectVisual project={project} />
            <div className="project-card-foot">
              <p>{project.description}</p>
              <div className="project-card-actions">
                <ul>{project.metrics.map((item) => <li key={item}>{item}</li>)}</ul>
                <a href={`/projects/${project.slug}`}>查看项目详情 <ArrowUpRight /></a>
              </div>
            </div>
          </article>
        ))}
      </div>
      <div className="app-projects-section">
        <div className="app-projects-header">
          <span className="section-kicker">应用项目与内容案例</span>
          <h3>让项目自己演示。<br />让能力看得见。</h3>
          <p>从一次真实操作，了解产品如何工作。任务、内容、学习与创作，在具体的使用场景里展开。</p>
          <span className="app-projects-count">{String(applicationProjects.length).padStart(2, '0')} PROJECTS / 动态项目展厅</span>
        </div>
        <div className="app-projects-grid app-projects-editorial" data-project-count={applicationProjects.length}>
          {applicationProjects.map((item, index) => {
            const demo = projectDemos[item.slug];
            return <article key={item.slug} className="app-project-card" data-layout={index % 2 ? 'text-media' : 'media-text'}>
              {demo ? <ProjectDemoView name={item.name} scenes={demo.scenes} {...projectViewerAssets[item.slug]} /> : <div className="app-project-media"><img src={item.images[0]} alt={`${item.name} 演示图`} loading="lazy" decoding="async" /></div>}
              <div className="app-project-content">
                <span className="app-project-number">{String(index + 1).padStart(2, '0')} / {demo?.platform ?? item.tag}</span>
                <h4>{item.name}</h4>
                <p>{demo?.intro ?? item.description}</p>
                {demo && <ul className="app-project-features">{demo.features.map(feature => <li key={feature}>{feature}</li>)}</ul>}
                <div className="app-project-foot">
                  <ul>{item.metrics.map((m) => <li key={m}>{m}</li>)}</ul>
                  <a href={`/projects/${item.slug}`}>查看详情 <ArrowUpRight /></a>
                </div>
              </div>
            </article>;
          })}
        </div>
      </div>
    </section>
  );
}

function ImageLightbox({ index, onClose, onMove }: { index: number; onClose: () => void; onMove: (next: number) => void }) {
  const [zoom, setZoom] = useState(1);
  const item = gptImages[index];

  useEffect(() => {
    setZoom(1);
  }, [index]);

  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose();
      if (event.key === 'ArrowLeft') onMove(index - 1);
      if (event.key === 'ArrowRight') onMove(index + 1);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [index, onClose, onMove]);

  return (
    <div className="lightbox" role="dialog" aria-modal="true" aria-label="GPT 图片大图预览">
      <button className="icon-button lightbox-close" type="button" onClick={onClose} aria-label="关闭大图"><X /></button>
      <button className="icon-button lightbox-prev" type="button" onClick={() => onMove(index - 1)} aria-label="上一张"><ChevronLeft /></button>
      <figure>
        <img src={item.src} alt="MSN GPT 图片作品大图" style={{ transform: `scale(${zoom})` }} />
        <figcaption><span>{String(index + 1).padStart(2, '0')} / {String(gptImages.length).padStart(2, '0')}</span><span>MSN · {item.series}</span></figcaption>
      </figure>
      <button className="icon-button lightbox-next" type="button" onClick={() => onMove(index + 1)} aria-label="下一张"><ChevronRight /></button>
      <div className="zoom-controls">
        <button type="button" onClick={() => setZoom((value) => Math.max(0.8, value - 0.2))} aria-label="缩小"><ZoomOut /></button>
        <span>{Math.round(zoom * 100)}%</span>
        <button type="button" onClick={() => setZoom((value) => Math.min(2.2, value + 0.2))} aria-label="放大"><ZoomIn /></button>
      </div>
    </div>
  );
}

function GalleryRow({ items, label, reverse, manual, onOpen }: {
  items: { src: string; series: string }[];
  label: string;
  reverse?: boolean;
  manual: number;
  onOpen: (src: string) => void;
}) {
  const ref = useRef<HTMLDivElement>(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ['start end', 'end start'] });
  const autoX = useTransform(scrollYProgress, [0, 1], reverse ? ['-12%', '3%'] : ['2%', '-13%']);
  const reduceMotion = useReducedMotion();

  return (
    <div className="gallery-row-shell">
      <div className="gallery-row-label">{label}</div>
      <div className="gallery-row-viewport" ref={ref}>
        <motion.div
          className="gallery-row"
          style={{ x: reduceMotion ? 0 : autoX, marginLeft: `${manual * -0.16}%` }}
        >
          {items.map((item, index) => (
            <button key={item.src} type="button" onClick={() => onOpen(item.src)} aria-label={`打开 ${item.series} 图片 ${index + 1}`}>
              <img src={item.src} alt="" loading="lazy" decoding="async" />
              <span className="image-watermark">MSN</span>
              <span className="image-expand" aria-hidden="true"><Maximize2 /></span>
            </button>
          ))}
        </motion.div>
      </div>
    </div>
  );
}

function GptGallery({ rhythmBaseline = false }: { rhythmBaseline?: boolean }) {
  const [manual, setManual] = useState(0);
  const [active, setActive] = useState<number | null>(null);
  const openBySource = (src: string) => setActive(gptImages.findIndex((item) => item.src === src));
  const move = (next: number) => setActive((next + gptImages.length) % gptImages.length);

  return (
    <section id="gpt-image" className="gallery-section dark-section">
      {rhythmBaseline
        ? <h2 className="sr-only">GPT-image2 三排横向作品展示</h2>
        : <SectionHeader
            index="01"
            kicker="AI 图片作品"
            title="三套 AI 视觉，一套横版展示系统"
            description="山雾饮料、智能扫地机与一致性真人套图统一为 16:9 横屏画面。"
          />}
      <div className="gallery-rows">
        <GalleryRow items={[...misteaImages]} label="MSN-AI 生成 · 山雾饮料" manual={manual} onOpen={openBySource} />
        <GalleryRow items={[...robotVacuumImages]} label="MSN-AI 生成 · 智能家电 / 扫地机器人" reverse manual={-manual} onOpen={openBySource} />
        <GalleryRow items={[...humanCcdImages]} label="MSN-AI 生成 · 真人一致性套图" manual={manual} onOpen={openBySource} />
      </div>
      <div className="gallery-control">
        <span>LEFT</span>
        <input
          type="range"
          min="-100"
          max="100"
          value={manual}
          onInput={(event) => setManual(Number(event.currentTarget.value))}
          onChange={(event) => setManual(Number(event.currentTarget.value))}
          aria-label="拖动横向浏览 GPT 图片"
        />
        <span>RIGHT</span>
      </div>
      <a className="gallery-detail-link" href="/gpt-image">查看 GPT-image2 系列详情 <ArrowRight /></a>
      {active !== null && <ImageLightbox index={active} onClose={() => setActive(null)} onMove={move} />}
    </section>
  );
}

function VideoGraphic({ poster, orientation, previewUrl, previewLabel }: {
  poster: string;
  orientation: string;
  previewUrl?: string;
  previewLabel: string;
}) {
  const hostRef = useRef<HTMLDivElement>(null);
  const videoRef = useRef<HTMLVideoElement>(null);
  const canHover = useMediaQuery('(hover: hover) and (pointer: fine)');
  const [isPreviewing, setIsPreviewing] = useState(false);
  const [previewReady, setPreviewReady] = useState(false);
  const [posterVisible, setPosterVisible] = useState(false);

  useEffect(() => {
    const host = hostRef.current;
    if (!host || typeof IntersectionObserver === 'undefined') {
      setPosterVisible(true);
      return;
    }
    const observer = new IntersectionObserver(([entry]) => {
      if (!entry.isIntersecting) return;
      setPosterVisible(true);
      observer.disconnect();
    }, { rootMargin: '480px 0px' });
    observer.observe(host);
    return () => observer.disconnect();
  }, []);

  const playPreview = () => {
    if (!canHover || !previewUrl) return;
    setPosterVisible(true);
    setIsPreviewing(true);
  };
  const stopPreview = () => {
    setPreviewReady(false);
    if (videoRef.current) {
      videoRef.current.pause();
      videoRef.current.currentTime = 0;
    }
    setIsPreviewing(false);
  };
  return (
    <div
      ref={hostRef}
      className={`video-graphic is-${orientation}`}
      data-previewing={previewReady}
      onMouseEnter={playPreview}
      onMouseLeave={stopPreview}
      onFocus={playPreview}
      onBlur={stopPreview}
    >
      <img src={posterVisible ? poster : undefined} data-src={poster} alt="" loading="lazy" decoding="async" />
      {canHover && isPreviewing && previewUrl && (
        <video
          className={previewReady ? 'is-previewing' : ''}
          ref={videoRef}
          src={previewUrl}
          poster={poster}
          muted
          loop
          playsInline
          autoPlay
          preload="none"
          aria-hidden="true"
          onCanPlay={() => setPreviewReady(true)}
          onError={stopPreview}
        />
      )}
      <span className="preview-label">{previewLabel}</span>
      <Play className="preview-play" aria-hidden="true" />
    </div>
  );
}

type PortfolioModalItem = Pick<PortfolioMediaItem, 'slug' | 'title' | 'category' | 'description' | 'videoUrl' | 'coverUrl' | 'orientation' | 'width' | 'height' | 'duration'> & {
  poster?: string;
  image?: string | null;
};

function PortfolioMediaModal({ items, index, onClose, onMove }: {
  items: readonly PortfolioModalItem[];
  index: number;
  onClose: () => void;
  onMove: (next: number) => void;
}) {
  const item = items[index];
  const poster = item.coverUrl || item.poster || item.image || undefined;
  const orientationLabel = item.orientation === 'vertical' ? '竖屏' : item.orientation === 'square' ? '方形' : '横屏';

  useEffect(() => {
    const scrollTop = window.scrollY;
    const root = document.documentElement;
    const body = document.body;
    const previous = {
      rootOverflow: root.style.overflow,
      bodyOverflow: body.style.overflow,
      bodyPosition: body.style.position,
      bodyTop: body.style.top,
      bodyWidth: body.style.width,
    };

    root.classList.add('has-video-modal');
    body.classList.add('has-video-modal');
    root.style.overflow = 'hidden';
    body.style.overflow = 'hidden';
    body.style.position = 'fixed';
    body.style.top = `-${scrollTop}px`;
    body.style.width = '100%';

    return () => {
      root.classList.remove('has-video-modal');
      body.classList.remove('has-video-modal');
      root.style.overflow = previous.rootOverflow;
      body.style.overflow = previous.bodyOverflow;
      body.style.position = previous.bodyPosition;
      body.style.top = previous.bodyTop;
      body.style.width = previous.bodyWidth;
      window.scrollTo({ top: scrollTop, left: 0, behavior: 'instant' });
    };
  }, []);

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose();
      if (event.key === 'ArrowLeft') onMove(index - 1);
      if (event.key === 'ArrowRight') onMove(index + 1);
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [index, onClose, onMove]);

  return createPortal(
    <div className={`video-modal-portal${document.documentElement.dataset.review === 'original-rhythm' ? ' review-original-rhythm' : ''}`}>
      <div
        className="video-modal"
        role="dialog"
        aria-modal="true"
        aria-label={`${item.title} 全屏媒体播放器`}
        onClick={(event) => { if (event.target === event.currentTarget) onClose(); }}
      >
        <div className={`video-modal-shell is-${item.orientation}`}>
          <div className="video-modal-toolbar">
            <span className="video-modal-toolbar-meta">{orientationLabel} · {index + 1}/{items.length}</span>
            <button type="button" className="video-modal-close" onClick={onClose} aria-label="关闭预览并返回作品集">
              <span>返回作品集</span>
              <span className="video-modal-key-hint">ESC</span>
              <X />
            </button>
          </div>
          <div
            className="video-modal-preview"
            onClick={(event) => { if (event.target === event.currentTarget) onClose(); }}
          >
            <video controls autoPlay playsInline preload="metadata" poster={poster} src={item.videoUrl}>当前浏览器不支持视频播放。</video>
          </div>
          <div className="video-modal-copy"><span>{item.category}</span><h3>{item.title}</h3><p>{item.description}</p><small>{item.width}×{item.height} · {item.duration.toFixed(1)} 秒 · {item.orientation.toUpperCase()}</small></div>
          <div className="video-modal-nav">
            <button type="button" onClick={() => onMove(index - 1)}><ChevronLeft /> 上一个</button>
            <span>{index + 1} / {items.length}</span>
            <button type="button" onClick={() => onMove(index + 1)}>下一个 <ChevronRight /></button>
          </div>
        </div>
      </div>
    </div>,
    document.body,
  );
}

function VideoRow({ items, categoryLabel, rowLabel, reverse, manual, offset, onOpen }: {
  items: readonly typeof videoItems[number][];
  categoryLabel: 'FILMING & EDITING' | 'AI MOTION';
  rowLabel: string;
  reverse?: boolean;
  manual: number;
  offset: number;
  onOpen: (index: number) => void;
}) {
  const ref = useRef<HTMLDivElement>(null);
  const trackRef = useRef<HTMLDivElement>(null);
  const [maxTravel, setMaxTravel] = useState(0);
  const { scrollYProgress } = useScroll({ target: ref, offset: ['start end', 'end start'] });
  const reduceMotion = useReducedMotion();
  const coarsePointer = useMediaQuery('(hover: none), (pointer: coarse)');
  const sliderProgress = (manual + 100) / 200;
  const rowProgress = reverse ? 1 - sliderProgress : sliderProgress;
  const manualBase = -maxTravel * rowProgress;
  const resolvedX = useTransform(scrollYProgress, (pageProgress) => {
    const pageTravel = Math.min(420, maxTravel * .08);
    const endpointInfluence = 1 - Math.min(1, Math.abs(manual) / 100);
    const pageOffset = (0.5 - pageProgress) * 2 * pageTravel * endpointInfluence * (reverse ? -1 : 1);
    return Math.min(0, Math.max(-maxTravel, manualBase + pageOffset));
  });

  useEffect(() => {
    const viewport = ref.current;
    const track = trackRef.current;
    if (!viewport || !track) return;

    let frame = 0;
    const updateTravel = () => {
      cancelAnimationFrame(frame);
      frame = requestAnimationFrame(() => {
        setMaxTravel(Math.max(0, track.scrollWidth - viewport.clientWidth));
      });
    };
    const observer = new ResizeObserver(updateTravel);
    observer.observe(viewport);
    observer.observe(track);
    updateTravel();
    window.addEventListener('resize', updateTravel);
    return () => {
      cancelAnimationFrame(frame);
      observer.disconnect();
      window.removeEventListener('resize', updateTravel);
    };
  }, [items]);

  return (
    <div className={`video-row-shell ${reverse ? 'is-reverse' : 'is-forward'}`}>
      <div className="video-row-label">{rowLabel}</div>
      <div ref={ref} className="video-row-viewport" data-row-category={categoryLabel}>
        <motion.div
          ref={trackRef}
          className="video-row-track"
          style={{ x: coarsePointer ? manualBase : (reduceMotion ? manualBase : resolvedX) }}
        >
          <div className="video-row-group">
            {items.map((item, index) => (
              <button
                key={item.slug}
                type="button"
                className="video-card"
                onClick={() => onOpen(offset + index)}
                data-preview-mode="hover"
                aria-label={`${categoryLabel}：${item.title}`}
              >
                <VideoGraphic poster={item.poster} orientation={item.orientation} previewUrl={item.previewUrl} previewLabel={item.previewLabel} />
                <span className="video-category-chip">{categoryLabel}</span>
                <span className="video-meta"><small>{item.category}</small><strong>{item.title}</strong><em>{item.description}</em></span>
              </button>
            ))}
          </div>
        </motion.div>
      </div>
    </div>
  );
}

function VideoWall() {
  const [manual, setManual] = useState(0);
  const [active, setActive] = useState<number | null>(null);
  const wallItems = [...filmingWorks, ...aiVideoWorks];
  const move = (next: number) => setActive((next + wallItems.length) % wallItems.length);

  return (
    <section id="video" className="video-section light-section">
      <SectionHeader
        index="02"
        kicker="视频作品"
        title="拍/剪与AI视频生成"
        titleNote="上轨拍/剪 X 下轨AI生成"
        description="上轨集中展示拍摄、剪辑与调色作品，下轨呈现 AI 视频生成项目。桌面端可随页面滚动横移并悬停预览；手机版保持轻量封面与顺滑滚动，点击卡片播放完整原片。"
      />
      <div className="video-rows">
        <VideoRow items={filmingWorks} categoryLabel="FILMING & EDITING" rowLabel="01 // FILMING & EDITING  拍/剪作品" manual={manual} offset={0} onOpen={setActive} />
        <VideoRow items={aiVideoWorks} categoryLabel="AI MOTION" rowLabel="02 // AI MOTION  AI 视频项目" reverse manual={manual} offset={filmingWorks.length} onOpen={setActive} />
      </div>
      <div className="video-control">
        <span>拖动浏览</span>
        <input type="range" min="-100" max="100" value={manual} onInput={(event) => setManual(Number(event.currentTarget.value))} onChange={(event) => setManual(Number(event.currentTarget.value))} aria-label="同步拖动浏览上下两排视频项目" />
        <a href="#video">全部视频 <ArrowRight /></a>
      </div>
      {active !== null && <PortfolioMediaModal items={wallItems} index={active} onClose={() => setActive(null)} onMove={move} />}
    </section>
  );
}

function Capabilities() {
  const variant = useActiveVariant();
  const profile = motionProfiles[variant];
  return (
    <section id="capabilities" className="capability-section light-section" aria-label="能力与工具">
      <SectionHeader
        index="04"
        kicker="能力清单"
        title="面向 AI 内容生产的实用能力组合"
        description="能力项只引用当前项目与本地工作流可核对的技术和交付行为，不声明未经验证的熟练度或商业成果。"
      />
      <div className="capability-list">
        {capabilities.map((item, index) => (
          <motion.article
            key={item.name}
            initial={profile.reveal}
            whileInView={{ opacity: 1, x: 0, y: 0, rotate: 0 }}
            viewport={{ once: true, amount: 0.38 }}
            transition={{ duration: profile.duration, delay: index * (variant === 'workflow' ? 0.09 : 0.045), ease: profile.ease }}
          >
            <span>{String(index + 1).padStart(2, '0')}</span>
            <h3>{item.name}</h3>
            <p>{item.detail}</p>
            <ArrowRight aria-hidden="true" />
          </motion.article>
        ))}
      </div>
    </section>
  );
}

function About() {
  const [copied, setCopied] = useState(false);
  const wechat = 'VYYDS678910';
  const copyWechat = async () => {
    await navigator.clipboard?.writeText(wechat);
    setCopied(true);
    window.setTimeout(() => setCopied(false), 1600);
  };

  return (
    <section id="about" className="about-section dark-section">
      <div className="about-copy">
        <span className="about-kicker">05 / 关于我</span>
          <h2><span>AI 内容创作</span><span>与工作流集成</span></h2>
          <p>立足视频制作与内容理解，借助 AI 工具进行需求拆解、工作流搭建、工具集成与调试验证。具备从选题构思、AI 辅助开发到成片与工具环境交付的完整实施能力。</p>
          <ul className="about-focus-list" aria-label="核心方向">
            <li>口播剪辑与包装工作流</li>
            <li>AI 视频生成与 3D 交互</li>
            <li>移动与本地 AI 图像服务</li>
            <li>工作流集成与交付维护</li>
        </ul>
      </div>
      <div id="contact" className="contact-panel">
          <span className="contact-eyebrow">求职沟通 / 作品交流</span>
          <h3><span>欢迎联系我</span></h3>
          <p>如需了解更多作品和项目经历，可以通过微信与我联系。</p>
        <div className="contact-method">
          <span>微信</span>
          <strong>{wechat}</strong>
        </div>
        <div className="contact-actions">
          <button type="button" onClick={copyWechat}><Copy /> {copied ? '已复制微信号' : '复制微信号'}</button>
          <a href="#projects">查看项目 <ArrowUpRight /></a>
        </div>
      </div>
      <footer>
        <span>MSN © 2026</span>
        <span>AI 视频 · AIGC 视觉 · 自动化工作流</span>
        <a href="#home">返回顶部 <ArrowUpRight /></a>
      </footer>
    </section>
  );
}

export default function App() {
  const pathname = window.location.pathname.replace(/\/+$/, '') || '/';
  const projectMatch = pathname.match(/^\/projects\/([^/]+)$/);
  if (projectMatch) return <ProjectDetailPage slug={projectMatch[1]} />;
  const toolMatch = pathname.match(/^\/tools\/([^/]+)$/);
  if (toolMatch) return <ToolDetailPage slug={toolMatch[1]} />;
  if (pathname === '/gpt-image') return <GptImageDetailPage />;

  const [variant, setVariant] = useState<Variant>(getInitialVariant);
  const rhythmBaseline = pathname === '/' || pathname === ORIGINAL_RHYTHM_PATH;
  const showKineticHero = ENABLE_3D_HERO && rhythmBaseline;
  const [introComplete, setIntroComplete] = useState(false);
  const [heroLoadProgress, setHeroLoadProgress] = useState(rhythmBaseline ? 8 : 100);
  const reportHeroLoadProgress = useCallback((progress: number) => {
    setHeroLoadProgress((current) => Math.max(current, progress));
  }, []);
  const handleIntroComplete = useCallback(() => setIntroComplete(true), []);

  useEffect(() => {
    document.documentElement.dataset.variant = variant;
    document.documentElement.dataset.review = rhythmBaseline ? 'original-rhythm' : '';
    const url = new URL(window.location.href);
    if (rhythmBaseline) url.searchParams.delete('variant');
    else url.searchParams.set('variant', variant);
    window.history.replaceState({}, '', url);
    document.title = rhythmBaseline ? 'LINZEXIN｜AI视觉导演与工作流作品集' : `MSN — ${variantMeta[variant].label}`;
  }, [rhythmBaseline, variant]);

  useEffect(() => {
    if (!rhythmBaseline) return;
    const previousRestoration = window.history.scrollRestoration;
    window.history.scrollRestoration = 'manual';
    const restoreEntrySection = () => {
      const targetId = window.location.hash.slice(1);
      const target = targetId ? document.getElementById(targetId) : null;
      if (target) target.scrollIntoView({ block: 'start', behavior: 'instant' });
      else window.scrollTo({ top: 0, behavior: 'instant' });
    };
    restoreEntrySection();
    const firstFrame = requestAnimationFrame(() => {
      restoreEntrySection();
      requestAnimationFrame(restoreEntrySection);
    });
    return () => {
      cancelAnimationFrame(firstFrame);
      window.history.scrollRestoration = previousRestoration;
    };
  }, [rhythmBaseline]);

  useEffect(() => {
    const selector = '.project-card, .video-card, .gallery-row button, .capability-list > article, .contact-panel, .hero-project-group > a, .detail-fact, .detail-sections > section, .detail-evidence-grid button, .video-modal-shell';

    const handlePointerMove = (e: PointerEvent) => {
      const card = (e.target as Element)?.closest?.(selector) as HTMLElement | null;
      if (!card) return;
      const rect = card.getBoundingClientRect();
      const x = e.clientX - rect.left;
      const y = e.clientY - rect.top;
      card.style.setProperty('--mouse-x', `${x}px`);
      card.style.setProperty('--mouse-y', `${y}px`);
      card.style.setProperty('--mouse-active', '1');
    };

    const handlePointerOut = (e: PointerEvent) => {
      const card = (e.target as Element)?.closest?.(selector) as HTMLElement | null;
      if (!card) return;
      if (!e.relatedTarget || !card.contains(e.relatedTarget as Node)) {
        card.style.setProperty('--mouse-active', '0');
      }
    };

    window.addEventListener('pointermove', handlePointerMove, { passive: true });
    window.addEventListener('pointerout', handlePointerOut, { passive: true });
    return () => {
      window.removeEventListener('pointermove', handlePointerMove);
      window.removeEventListener('pointerout', handlePointerOut);
    };
  }, []);

  const appClass = useMemo(
    () => `app variant-${variant}${rhythmBaseline ? ' review-original-rhythm' : ''}`,
    [rhythmBaseline, variant],
  );

  return (
    <VariantContext.Provider value={variant}>
      <main className={appClass} aria-busy={rhythmBaseline && !introComplete}>
        {rhythmBaseline && <PortfolioIntro criticalProgress={heroLoadProgress} onComplete={handleIntroComplete} />}
        {!showKineticHero && <Navigation variant={variant} onVariantChange={setVariant} rhythmBaseline={rhythmBaseline} />}
        {showKineticHero
          ? <KineticHero onLoadProgress={reportHeroLoadProgress} />
          : <Hero variant={variant} workingBaseline={rhythmBaseline} onLoadProgress={reportHeroLoadProgress} />}
        <GptGallery rhythmBaseline={rhythmBaseline} />
        <VideoWall />
        <CoreProjects rhythmBaseline={rhythmBaseline} />
        <Capabilities />
        <About />
      </main>
    </VariantContext.Provider>
  );
}
